import { useActivities } from 'containers/CRM/hooks/useActivities';
import { useActivityFilters } from 'containers/CRM/hooks/useActivityFilters';
import React, { useCallback, useEffect } from 'react';
import { ActivityFiltersAndButtons } from '../ActivityFilters/ActivityFiltersAndButtons';
import { VerticalSpace } from 'ui/VerticalSpace';
import { ActivityList } from '../ActivityList/ActivityList';
import { PageTitle } from 'ui/PageTitle';
import { useSelector } from 'react-redux';
import { isAdmin as isAdminSelector, getCurrentUserUuid } from 'store/modules/auth';
import { noop } from 'utils';
import { ICrmActivity } from 'services/CrmApi/types/CrmActivityTypes';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';

export const CrmMyActivity: React.FC = () => {
  const isAdmin = useSelector(isAdminSelector);
  const currentUserId = useSelector(getCurrentUserUuid);
  const { dynamicParameters } = useDynamicParameters();
  const {
    activityFilters,
    dispatchActivityFilters,
    resetHighlightedActivity,
    highlightedActivityId,
    setHighlightedActivityId,
    handleSortOrderChange,
  } = useActivityFilters(dynamicParameters.ENABLE_CRM_TMS);
  const { activities, activitiesCount, handleLoadMore, crmActivities } = useActivities(
    undefined,
    activityFilters,
    dispatchActivityFilters
  );

  const refetchActivities = useCallback(() => {
    crmActivities.refetch();
  }, [crmActivities]);

  const handleEditActivity = useCallback(
    async (activity?: ICrmActivity) => {
      if (activity) {
        dispatchActivityFilters({ type: 'set_cursor', value: activity.id });
        if (highlightedActivityId && highlightedActivityId === activity.id) {
          await crmActivities.refetch();
        }
        setHighlightedActivityId(activity.id);
      }
    },
    [crmActivities]
  );

  useEffect(() => {
    if(!isAdmin) {
      dispatchActivityFilters({type: 'set_created_by_id', value: currentUserId as string});
    }  
  }, []);

  useEffect(() => {
    if (crmActivities.isError) {
      resetHighlightedActivity();
    }
  }, [crmActivities.isError, resetHighlightedActivity]);

  return (
    <div className="flex flex-col border border-solid border-gray-40 border-t-0 pb-30px px-5">
      <PageTitle
        primaryText={isAdmin ? 'Activity' : 'My Activity'}
        secondaryCondition={!crmActivities.isFetching}
        secondaryText={` (${activitiesCount} results found)`}
      />
      <ActivityFiltersAndButtons
        directoryEntryId={undefined}
        directoryEntryType={undefined}
        activityFilters={activityFilters}
        dispatchActivityFilters={dispatchActivityFilters}
        activityFiltersDisabled={crmActivities.isFetching || crmActivities.isFetchingNextPage}
        onCreateActivity={noop}
        resetHighlightedActivity={resetHighlightedActivity}
      />
      <VerticalSpace height="20px" />
      <ActivityList
        activities={activities ?? []}
        count={activitiesCount}
        highlightedActivityId={highlightedActivityId}
        onLoadMore={handleLoadMore}
        isLoading={crmActivities.isFetching}
        isFetchingNextPage={crmActivities.isFetchingNextPage}
        sortOrder={activityFilters.sort.order}
        directoryEntryId={undefined}
        directoryEntryType={undefined}
        onSortOrderChange={handleSortOrderChange}
        onEditActivity={handleEditActivity}
        onDeleteActivity={refetchActivities}
      />
    </div>
  );
};
